<script setup lang="ts">
import { computed } from 'vue';
import VSidebar from '@shared/component/VSidebar/VSidebar.vue';
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import {
  type AdviseurbeheerRouteRecord,
  type AdviseurMenuGroup,
} from '@crm-lib/routes/adviseurbeheer.routes';
import {
  faEnvelope,
  faPhoneFlip,
  faUserTie,
  faExclamationTriangle,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import VStatusTag from '@shared/component/VStatusTag/VStatusTag.vue';
import VTooltip from '@shared/component/VTooltip/VTooltip.vue';
import { type VSidebarItem } from '@shared/component/VSidebar/utils';
import { useClassicEmbedReset } from '@crm-lib/composables/useClassicEmbedReset';
import { useAdviseur } from '../../composables/adviseur';
import { useAdviseurMenuTellingen } from '../../composables/adviseurMenuTellingen';

const props = defineProps<{
  routes: AdviseurbeheerRouteRecord[];
}>();

const { data: adviseur, error, isPending } = useAdviseur();
useWatchErrorHandler(error);

const { countPerRoute } = useAdviseurMenuTellingen();

const { triggerReset } = useClassicEmbedReset();

function onItemClick({ isActive }: { item: VSidebarItem; isActive: boolean }) {
  if (isActive) triggerReset();
}

const groupOrder: AdviseurMenuGroup[] = ['Algemeen', 'Profiel'];

const menuItems = computed(() => {
  const items = props.routes.map((route) => ({
    label: route.meta.label,
    icon: route.meta.icon,
    route,
    group: route.meta.group,
    indicator: typeof route.name === 'string' ? countPerRoute.value[route.name] : undefined,
  }));

  const grouped = items.reduce(
    (acc, item) => {
      (acc[item.group] ??= []).push(item);
      return acc;
    },
    {} as Record<AdviseurMenuGroup, typeof items>,
  );

  return groupOrder
    .filter((groupName) => grouped[groupName]?.length > 0)
    .map((groupName) => ({
      label: groupName,
      items: grouped[groupName],
    }));
});

const headerTitle = computed(() => adviseur.value?.naam ?? 'Laden...');

const headerMetadata = computed(() => {
  if (!adviseur.value) return [];
  const items = [];

  if (adviseur.value.emailadres) {
    items.push({
      icon: faEnvelope,
      label: adviseur.value.emailadres,
      href: `mailto:${adviseur.value.emailadres}`,
    });
  }

  if (adviseur.value.telefoonnummer) {
    items.push({
      icon: faPhoneFlip,
      label: adviseur.value.telefoonnummer,
      href: `tel:${adviseur.value.telefoonnummer}`,
    });
  }

  return items;
});
</script>
<template>
  <VSidebar
    :items="menuItems"
    :header-icon="faUserTie"
    header-icon-color="orange"
    :header-title="headerTitle"
    :header-metadata="headerMetadata"
    :header-loading="isPending"
    @item-click="onItemClick"
  >
    <template #countBadge="{ indicator }">
      <VTooltip
        v-if="indicator && (indicator.variant === 'danger' || indicator.count)"
        :label="indicator.tooltip"
        :active="!!indicator.tooltip"
        theme="primary"
        class="ml-auto"
      >
        <FontAwesomeIcon
          v-if="indicator.variant === 'danger' && !indicator.count"
          :icon="faExclamationTriangle"
          size="lg"
          class="text-orange"
        />
        <VStatusTag v-else :color="indicator.variant === 'danger' ? 'orange' : 'grey'" size="sm">
          {{ indicator.count }}
        </VStatusTag>
      </VTooltip>
    </template>
  </VSidebar>
</template>
